import { useMemo } from 'react';
import { makeStyles } from '@material-ui/core';
import { StatCard, StatCardTone } from '../StatCard';
import { boschGray } from '../../../modules/theme/boschColors';
import { useApiData } from './useApiData';
import { getJsonPath, toDisplayPrimitive } from './jsonPath';
import { ApiStatusBar } from './ApiStatusBar';

const useStyles = makeStyles(() => ({
  /*
   * Wrap the underlying StatCard so we can append our status bar
   * underneath WITHOUT modifying the visual of the static StatCard
   * (we still want the static widget to look identical for inline
   * pages without API integration).
   */
  wrapper: {
    border: `1px solid ${boschGray[90]}`,
    borderRadius: 6,
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
    display: 'flex',
    flexDirection: 'column',
  },
  /* Force the inner StatCard to drop its own border/border-radius
   * so the wrapper's borders are the only ones the user sees. */
  inner: {
    '& > div': {
      border: 'none',
      borderRadius: 0,
      transition: 'none',
    },
    '& > div:hover': {
      boxShadow: 'none',
    },
  },
}));

export type ApiStatCardProps = {
  label: string;
  url?: string;
  inlineJson?: string;
  /** Path to the numeric/string value displayed as the big number. */
  valuePath: string;
  /** Optional path to a delta string (e.g. "+12%"). */
  deltaPath?: string;
  unit?: string;
  tone?: StatCardTone;
  refreshSeconds?: number;
};

/**
 * Live KPI tile — same visual as the static `stat-card`, but with
 * the value (and optional delta) wired to a JSON response. Useful
 * for things like:
 *
 *   - "Active components: <count from catalog>"
 *   - "Pipeline success rate: <% from Azure DevOps>"
 *   - "Open incidents: <count from PagerDuty>"
 *
 * Why we don't reuse `<StatCard>` 1:1 and instead wrap it: we need
 * to overlay an ApiStatusBar with refresh / error info, which would
 * pollute the static widget's surface if added there. Composition
 * keeps both widgets cohesive.
 */
export const ApiStatCard = ({
  label,
  url,
  inlineJson,
  valuePath,
  deltaPath,
  unit,
  tone = 'neutral',
  refreshSeconds,
}: ApiStatCardProps) => {
  const classes = useStyles();
  const { raw, loading, error, lastUpdatedAt, refetch } = useApiData({
    url,
    inlineJson,
    refreshSeconds,
  });

  /* We resolve the two paths independently against `raw` (instead of
   * using the hook's single `path` and a path inside the StatCard)
   * because a stat tile typically wants TWO points from the same
   * response — the value AND the delta. Doing two fetches for two
   * fields would be wasteful. */
  const { displayValue, displayDelta } = useMemo(() => {
    const valueRaw = getJsonPath(raw, valuePath);
    const deltaRaw = deltaPath ? getJsonPath(raw, deltaPath) : undefined;
    return {
      displayValue: toDisplayPrimitive(
        valueRaw,
        loading ? '…' : error ? '!' : '—',
      ),
      displayDelta:
        deltaRaw !== undefined && deltaRaw !== null
          ? toDisplayPrimitive(deltaRaw, '')
          : undefined,
    };
  }, [raw, valuePath, deltaPath, loading, error]);

  return (
    <div className={classes.wrapper}>
      <div className={classes.inner}>
        <StatCard
          label={label || 'Live KPI'}
          value={displayValue}
          unit={unit}
          delta={displayDelta || undefined}
          tone={tone}
        />
      </div>
      <ApiStatusBar
        loading={loading}
        error={error}
        lastUpdatedAt={lastUpdatedAt}
        onRefresh={url ? refetch : undefined}
        modeLabel={url ? 'Live API' : 'Inline JSON'}
      />
    </div>
  );
};
